import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { ArrowLeft, Download, Clock, Ticket as TicketIcon, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PrizeTicket } from "@/components/PrizeTicket";
import { toast } from "sonner";

function remainingText(expiresAt, now) {
  if (!expiresAt) return { text: "No expiry", expired: false, urgent: false };
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return { text: "Expired", expired: true, urgent: false };
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const text = h > 0
    ? `${h}h ${m}m left`
    : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")} left`;
  return { text, expired: false, urgent: ms < 60000 };
}

function OfferCard({ offer, now, highlight }) {
  const ref = useRef(null);
  const { toggleRedeem } = useApp();
  const { text, expired, urgent } = remainingText(offer.expiresAt, now);
  const redeemed = !!offer.redeemed;

  const save = async () => {
    if (!ref.current) return;
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const a = document.createElement("a");
      a.download = `appy-hour-offer-${offer.code}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Ticket saved!");
    } catch {
      toast.error("Couldn't save image.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`offer-card-${offer.code}`}
      className={`rounded-3xl ${highlight ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-950" : ""} ${expired && !redeemed ? "opacity-50 grayscale" : ""}`}
    >
      <div className="relative">
        <PrizeTicket ref={ref} offer={offer} />
        <AnimatePresence>
          {redeemed && (
            <motion.div
              initial={{ opacity: 0, scale: 1.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              data-testid={`offer-redeemed-stamp-${offer.code}`}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <span className="rotate-[-14deg] border-4 border-red-600 text-red-600 font-display font-black text-4xl uppercase tracking-widest px-6 py-2 rounded-xl bg-white/40">
                Redeemed
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between mt-3 px-1">
        <span className={`flex items-center gap-1.5 text-sm font-bold ${redeemed ? "text-slate-500" : expired ? "text-slate-500" : urgent ? "text-red-400" : "text-amber-400"}`}>
          <Clock size={15} /> {redeemed ? "Redeemed" : text}
        </span>
        <button
          data-testid={`offer-save-${offer.code}`}
          onClick={save}
          className="flex items-center gap-1.5 text-sm font-bold text-slate-200 bg-slate-800 rounded-full px-4 py-2 no-tap-highlight"
        >
          <Download size={15} /> Save
        </button>
      </div>

      {/* Staff-only big red nuke-style redeem toggle */}
      <motion.button
        data-testid={`offer-redeem-${offer.code}`}
        whileTap={{ scale: 0.94 }}
        onClick={() => {
          toggleRedeem(offer.code);
          toast[redeemed ? "message" : "success"](redeemed ? "Coupon un-redeemed" : "💥 Coupon redeemed!");
        }}
        className={`mt-3 w-full h-16 rounded-2xl font-display font-black text-xl uppercase tracking-wider flex items-center justify-center gap-3 no-tap-highlight border-2 transition-colors ${
          redeemed
            ? "bg-slate-800 text-green-400 border-green-500/40"
            : "bg-red-600 text-white border-red-400 shadow-[0_0_30px_rgba(220,38,38,0.6)] animate-pulse"
        }`}
      >
        <ShieldCheck strokeWidth={2.5} size={24} />
        {redeemed ? "Redeemed — tap to undo" : "Staff: Redeem Now"}
      </motion.button>
      <p className="text-[11px] text-slate-500 text-center mt-2">
        {redeemed ? "This coupon has been used." : "Bar staff only — press to mark this coupon as used."}
      </p>
    </motion.div>
  );
}

export default function Offers() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlightCode = params.get("code");
  const { profile } = useApp();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const offers = profile?.offers || [];

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-2 pt-4">
        <button data-testid="offers-back-btn" onClick={() => navigate("/")} className="text-slate-400 p-1">
          <ArrowLeft strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-3xl font-black text-slate-50">My Coupons</h1>
      </div>
      <p className="text-slate-400 mb-6">Show a coupon at the bar to redeem it before it expires.</p>

      {offers.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <TicketIcon size={44} className="mx-auto mb-3 opacity-40" />
          No coupons yet. Win one at a partner venue quiz!
        </div>
      ) : (
        <div className="space-y-8" data-testid="offers-list">
          {offers.map((o) => (
            <OfferCard key={o.code} offer={o} now={now} highlight={o.code === highlightCode} />
          ))}
        </div>
      )}
    </div>
  );
}
